<template>
  <a-form class="mfa-input-form" @finish="validateCode">
    <div class="mfa-step">
      <div class="mfa-step_header">
        <div>
          <img 
            :src="typeMfa == MfaType.Google ? googleAuth : lockIcon"
            width="64" alt="Google Auth"
          />
        </div>
        <h2>
          <span v-if="typeMfa == MfaType.Google">
            {{
              !hideBack ?
              'Valida el código de verificación' :
              'Ingresa el código de verificación de Google Authenticator'
            }}
          </span>
          <span v-else>
            {{
              !hideBack ?
              'Valida el código de verificación' :
              'Ingresa el código de verificación'
            }}
          </span>
        </h2>
        <div v-if="typeMfa == MfaType.Google">
          <p v-if="!hideBack">
            Ingresa el código de verificación de 6 dígitos que aparece en la <br/>
            aplicación de Google Authenticator.
          </p>
          <p v-else>
            Desde la aplicación Google Authenticator, visualiza el código de <br/>
            verificación de 6 dígitos de tu cuenta e ingrésalo a continuación.
          </p>
        </div>
        <div v-else>
          <p v-if="!hideBack">
            Hemos enviado un código de verificación de 6 dígitos al correo <br/>
            electrónico “{{emailForm}}”.
          </p>
          <p v-else>
            Ingresa el código de verificación que hemos enviado al correo <br/> 
            electrónico “{{emailUser}}”.
          </p>
        </div>      
      </div>
      <a-input-group compact>
        <a-input
          v-for="(n, index) in 6"
          :key="n"
          v-model:value="code[index]"
          maxlength="1"
          class="mfa-code-input"
          :class="{ 'mfa-code-input_invalid': isValidCode === false }"
          :ref="(el: any) => setInputRef(el, index)"
          @input="onInput(index)"
          @keydown.backspace="onBackspace(index)"
          @keydown.enter="validateCode"
          @paste="onPaste"
        />
        <!-- @keydown="onArrowKey(index, $event)" -->
      </a-input-group>
      <p  v-if="typeMfa == MfaType.Email" class="mfa-step_info">
        ¿No recibiste el código?
        <a-typography-link
          :disabled="resendSeconds > 0 || loadingResend"
          @click="handleResend"
          style="cursor: pointer;"
          :class="{ 'resend-disabled': resendSeconds > 0 || loadingResend }"
        >
          Reenviar código
          <span v-if="resendSeconds > 0">({{ resendSeconds }}s)</span>
        </a-typography-link>
      </p>
      <div class="mfa-step_footer">
        <a-button
          type="primary"
          html-type="submit"
          :loading="loading"
          :disabled="!isValidCodeRequired || loading || loadingLogin"
          @click="validateCode"
        >
          {{ loadingLogin ? 'Iniciando sesión' : !loading ? 'Validar código' : 'Validando código'}}
        </a-button>
        <a-button v-if="!hideBack" @click="$emit('back')">Regresar</a-button>
      </div>
      <div class="mfa-help">
        <a-typography-link @click="showModal = true">
          Necesito ayuda con mi código
        </a-typography-link>
      </div>
    </div>
  </a-form>

  <a-modal
    v-model:visible="showModal"
    width="560px"
    class="help"
    :footer="null"
    centered
  >
    <div>
      <img :src="lockIcon" width="64" alt="Google Auth" />
    </div>
    <h2 class="help_title">
      Ayuda con mi código
    </h2>
    <p>
      Si perdiste el acceso a tu código de verificación o presentas otros 
      inconvenientes relacionados a este, puedes realizar lo siguiente:
    </p>
    <a-card style="margin-bottom: 10px">
      <h3 class="help_subtitle">
        <QuestionCircleOutlined />
        Contactar a Soporte Beex
      </h3>
      <span>
        Solicita ayuda a tu administrador para que te comunique con 
        nuestra área de soporte, o genere un ticket de ayuda.
      </span>
    </a-card>
    <a-card>
      <h3 class="help_subtitle">
        <LockOutlined />
        Reiniciar autenticación en 2 pasos (2FA)
      </h3>
      <span>
        Si perdiste tu token, solicita a tu administrador que te genere 
        una nueva contraseña para reiniciar la autenticación en 2 pasos.
      </span>
    </a-card>
    <!-- <h4>
      Para mayor soporte, contáctanos al siguiente número:
    </h4>
    <a :href="whatsappSupport" target="_blank" style="font-size: 18px; font-weight: 600;">
      <WhatsAppOutlined />
      +51 {{ numberSupport }}
    </a> -->
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, toRaw, inject } from 'vue'
import { fetchDataUser } from "@/app/auth/services";
import googleAuth from "@/app/auth/assets/google_auth.png?url";
import lockIcon from "@/app/auth/assets/lock_icon.png?url";
import { mfaService } from "@/app/auth/services/MfaService";
import { MfaType } from '../../utils/enums';
import { message } from 'ant-design-vue';
import { useStore } from "vuex";
import { useRouter } from 'vue-router';
import items from "@/app/common/components/organisms/TheSidebar/items";
import { registerEventManagement } from '@/app/shared/services';
import { EventManagement, StateOptions } from '@/app/shared/utils/enums';
import { notification } from 'ant-design-vue';
import { ResponseError } from '@/app/shared/utils/httpManager';
// import IconTeamOutlined from '../atoms/IconTeamOutlined.vue';
// import { WhatsAppOutlined } from '@ant-design/icons-vue';
import { LockOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import { safeRegisterEventManagement } from '@/app/shared/composables/useRegisterEventManagement';

const props = defineProps({
  hideBack: {
    type: Boolean,
    default: false,
  },
  emailForm: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['back']);

const showModal = ref(false);
const store = useStore();
const router = useRouter();
const typeMfa = inject('typeMfa') as any;
// const whatsappSupport = `https://wa.me/51${import.meta.env.VITE_SUPPORT_WHATSAPP}`;
// const numberSupport = import.meta.env.VITE_SUPPORT_WHATSAPP;

const code = ref(Array(6).fill(''))
const loading = ref(false);
const loadingLogin = ref(false);
const loadingResend = ref(true);

const isValidCodeRequired = computed(() => code.value.every(char => char.match(/^\d$/)))
const isValidCode = ref<boolean | null>(null);
const emailUser = ref<string>();
const inputRefs = ref<HTMLInputElement[]>([])

const resendSeconds = ref(0);
const resendInterval = ref<number | null>(null);

function setInputRef(el: HTMLInputElement, index: number) {
  inputRefs.value[index] = el
}

function onPaste(event: ClipboardEvent) {
  const pastedText = event.clipboardData?.getData('text') || ''
  console.log(pastedText, 'pasted text');
  if (/^\d{6}$/.test(pastedText)) {
    event.preventDefault()
    for (let i = 0; i < 6; i++) {
      code.value[i] = pastedText[i]
    }
    // Opcional: mover el focus al final o al siguiente input vacío
    const nextEmptyIndex = code.value.findIndex(c => c === '')
    if (nextEmptyIndex !== -1) {
      inputRefs.value[nextEmptyIndex]?.focus()
    } else {
      inputRefs.value[5]?.focus()
    }
  }
}

const startResendCountdown = () => {
  resendSeconds.value = 30;
  resendInterval.value = window.setInterval(() => {
    resendSeconds.value--;
    if (resendSeconds.value <= 0 && resendInterval.value) {
      clearInterval(resendInterval.value);
      resendInterval.value = null;
    }
  }, 1000);
};

const handleResend = async () => {
  if (resendSeconds.value === 0 && !loadingResend.value) {
    loadingResend.value = true;
    await handleSendEmail();
  }
};

const onInput = (index: number) => {
  if (code.value[index].length === 1 && index < code.value.length - 1) {
    nextTick(() => {
      inputRefs.value[index + 1]?.focus()
    })
  }
}

const handleSendEmail = async () => {
  console.log("handleSendEmail");
  const storedData = window.atob(sessionStorage.getItem("authenticationData") || '');
  const userData = JSON.parse(storedData);
  try {
    await mfaService.generateToken({
      username: userData?.username,
      password: userData?.password,
      mfa_type: MfaType.Email,
      email: props.emailForm ?? userData?.username,
    });
    startResendCountdown();
  } catch (error) {
    const messageError =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(messageError);
  } finally {
    loadingResend.value = false;
  }
}

// const onArrowKey = (index: number, event: KeyboardEvent) => {
//   if (event.key === 'ArrowRight' && index < inputRefs.value.length - 1) {
//     nextTick(() => {
//       inputRefs.value[index + 1]?.focus()
//     })
//   } else if (event.key === 'ArrowLeft' && index > 0) {
//     nextTick(() => {
//       inputRefs.value[index - 1]?.focus()
//     })
//   }
// }

const onBackspace = (index: number) => {
  if (code.value[index] === '' && index > 0) {
    nextTick(() => {
      inputRefs.value[index - 1]?.focus()
    })
  }
}

const userDataLogin = () => {
  const storedData = window.atob(sessionStorage.getItem("authenticationData") || '');
  const userData = JSON.parse(storedData);
  return {
    username: userData?.username,
    password: userData?.password,
  };
}

const validateCode = async () => {
  if (isValidCodeRequired.value) {
    const finalCode = code.value.join('')
    try {
      loading.value = true;
      const userData = userDataLogin()
      const response = await mfaService.verifyToken({
        ...userDataLogin(),
        token: finalCode,
        email: props.emailForm ?? userData?.username,
        mfa_type: typeMfa.value,
      });
      isValidCode.value = true;
      console.log('Response from MFA validation:', response);
      message.success("Validación de código exitosa.");
      loadingLogin.value = true;
      await handleLogIn();
      loading.value = false;
      loadingLogin.value = false;

    } catch (error: any) {
      loading.value = false;
      if (error.data?.valid === false ) {
        message.error("Código de verificación incorrecto. Intenta nuevamente.");
      } else {
        const messageError =
          error instanceof ResponseError ? error.getErrorMessage() : "Código de verificación incorrecto. Intenta nuevamente.";

        message.error(messageError);
      }
      isValidCode.value = false;
    }
  }
}

const goIntoApp = async (user: any) => {
  try {
    // const menu = items.find((item) =>
    //   user.options_menu.find((_menu) => _menu.scope === item.scope)
    // );

    const firstUserMenu = user.options_menu[0];
    const firstUserSubmenu = firstUserMenu?.items?.[0];

    const menusList = items.flatMap((item: any) => {
      const menus = [item];

      if (item.subitems) menus.push(...item.subitems);

      return menus;
    });

    // const path = firstUserSubmenu
    //   ? menusList.find((item) => item.scope === firstUserSubmenu.scope)
    //   : menusList.find((item) => item.scope === firstUserMenu.scope);

    const path = menusList.find((item) =>
      firstUserSubmenu
        ? item.scope === firstUserSubmenu.scope
        : item.scope === firstUserMenu.scope
    );

    console.log({ menusList, firstUserMenu, firstUserSubmenu, path });

    // const path = menu.route;
    localStorage.removeItem("tabCount");
    await safeRegisterEventManagement(EventManagement.Login, {
      state: StateOptions.Connected,
      module: 'mfa-validate-code'
    });
    router.push(path.route);
  } catch (error) {
    console.log(error);
  }
};

const handleLogIn = async () => {
  const formUser = userDataLogin();
  try {
    loading.value = true;
    sessionStorage.removeItem('tab_browser')
    sessionStorage.removeItem('tabrowser_id')
    localStorage.removeItem('history_tabs')
    await store.dispatch("fetchAccessToken", toRaw({
      username: props.emailForm ?? formUser.username,
      password: formUser.password,
    }));
    const response: any = await fetchDataUser();
    if (response.token)
      router.push(`/user/register/${formUser.password}/?changePassword=true`);
    else await goIntoApp(response);
  } catch (error: Error | any) {
    console.error(error);
    console.log(error.data);

    if (!error.status) {
      message.error("El recaptcha no es válido, refresque y vuelva a intentarlo");
      loading.value = false;
    }

    switch (error.status) {
      case 400: {
        // SET DATA FOR LOGIN AFTER PAY EXPIRED BILLING
        if (error.data.code == "BILLING") {
          const encodedAuthData = window.btoa(
            `${formUser.username}:${formUser.password}`
          );

          sessionStorage.setItem("authenticationData", encodedAuthData);
          sessionStorage.setItem("userEmail", formUser.username);

          const parsedQueryParams = new URLSearchParams(error.data.data);
          console.log(parsedQueryParams.toString(), "parsedQueryParams");
          router.push(`/billing/expired?${parsedQueryParams.toString()}`);
          break;
        } else if (error.data.code == "USER") {
          router.push(`/billing/expired?user=disabled`);
          break;
        } else if (error.data.code == "MFA") {
          const encodedAuthData = window.btoa(JSON.stringify(formUser));

          sessionStorage.setItem("authenticationData", encodedAuthData);
          sessionStorage.setItem("mfaData", JSON.stringify(error.data.data));
          router.push('/login/mfa');
          break;
        } else {
          notification.error({
            message: error?.getErrorMessage(),
          });
          break;
        }
      }

      case 401: {
        notification.error({
          message: "El usuario o la contraseña no son correctos",
        });
        break;
      }

      case 403: {
        console.log(formUser);
        router.push(`/user/register/${formUser.password}/?generatedUser=true`);
        break;
      }

      default: {
        message.error("Ha ocurrido un error");
        break;
      }
    }
  } finally {
    loading.value = false;
  }
};

// Enfocar el primer input al montar el componente
onMounted(() => {
  const storedData = window.atob(sessionStorage.getItem("authenticationData") || '');
  const userData = JSON.parse(storedData);
  emailUser.value = userData?.username || '';
  if (typeMfa.value == MfaType.Email) {
    if (props.hideBack) { // despues de la primera verificacion
      handleSendEmail();
    } else { 
      startResendCountdown();
    }
  }
  nextTick(() => {
    inputRefs.value[0]?.focus()
  })
})
</script>

<style scoped lang="sass">
.mfa-step
  text-align: center
  flex-direction: column
  display: flex
  gap: 24px
  justify-content: center
  justify-items: center
  align-items: center
  &_header
    display: flex
    flex-direction: column
    gap: 16px
    text-align: center
    justify-items: center
    align-items: center
  h2
    width: 400px
    font-weight: 600
    margin-bottom: 0
    font-size: 24px
    line-height: 32px
  p
    margin: 0
    color: #000000A6
    font-size: 14px

  .mfa-code-input
    width: 36px
    height: 48px
    text-align: center
    margin: 0 10px
    border-radius: 6px !important
    &_invalid
      border: 1px solid #FFA39E !important

  &_footer
    width: 420px
    display: flex
    flex-direction: column
    gap: 16px
    text-align: center

  &_info
    width: 350px

  .mfa-stores
    display: flex
    justify-content: center
    gap: 1rem

    img
      height: 40px

  .mfa-help
    margin-top: 8px
    font-size: 14px
    color: #000000A6
    display: flex
    gap: 8px
    align-items: center
    a
      display: flex
      gap: 6px
      align-items: center

.resend-disabled
  color: #69b1ff !important
  cursor: not-allowed !important

:global(.help .ant-modal-body)
  text-align: center !important
  padding: 20px 50px

.help
  &_title
    margin: 20px 40px
  &_subtitle
    display: flex
    align-items: center
    gap: 5px
    justify-content: center
  &_list
    text-align: left
    h4
      margin-bottom: 12px
    &--item
      display: flex
      gap: 5px
    > div
      display: flex
      flex-direction: column
      gap: 10px
:global(.help .ant-card-body)
  padding: 15px !important
</style>
